import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useMatch } from 'react-router-dom';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import type { Role } from '@commissions/auth/roles';
import { PAGES, TAB_IDS } from '@commissions/auth/pages';
import { appConfig, loginRequest, msalConfigured } from '@/auth/msal';
import { Badge } from '@/ui-kit/atoms/badge';
import { roleTone } from '@/app-kit/role-tone';
import { Button } from '@/ui-kit/atoms/button';
import { LoadingSpinner } from '@/ui-kit/atoms/loading-spinner';
import { Logo } from '@/ui-kit/atoms/logo';
import { CurrentUserProvider } from '@/app-kit/current-user-context';
import { useCurrentUser, type CurrentUser } from '@/app-kit/use-current-user';
import { HttpError, setDevBypassRoleHeader, setViewAsRoleHeader } from '@/app-kit/request-service';
import { useScrollRestore } from '@/app-kit/scroll-restore';

import { TimeZoneProvider } from '@/app-kit/timezone-context';
import { TimeZoneSelect } from '@/app-kit/timezone-select';
import { NoAccessPage } from '@/pages/no-access-page';
import { SessionExpiredPage, REAUTH_FLAG } from '@/pages/session-expired-page';
import { BugReportButton } from '@/observability/bug-report-button';
import { TestInstanceNotice } from './test-instance-notice';

// The dev-bypass picker's options — the HUMAN roles, plus `Local`. Machine
// roles (`allowedMemberTypes: ["Application"]` in ensure-aad-app.sh) are
// deliberately absent: no person holds one, and offering it would let a local
// session impersonate the worker's identity, which reaches no page by design.
//
// `Local` is the inverse case — a role with NO Entra counterpart at all, so no
// deployed token can carry it. It holds the surfaces that exist only on this
// workstation (the AP/payments family, reviews, the commission engine), and is
// meant to be ticked ALONGSIDE a real role: grants union, so Admin + Local is
// Admin plus those. See its entry in roles.jsonc.
//
// ⚠️ HARDCODED, and it must track packages/auth/policy/roles.jsonc. A role
// missing here never appears in the dev picker or the view-as picker, so it
// cannot be exercised locally at all — silently, because an absent checkbox
// looks like a shorter list rather than a bug.
//
// The four legacy names (Admin/Approver/Analyst/Viewer) were dropped on
// 2026-08-18, when their definitions were deleted from staging's Entra app
// registration. Offering a checkbox for a role that no longer exists is worse
// than omitting it: the session it fabricates holds an unknown role, which is
// deny-by-default, so the app renders empty and reads as a broken build.
export const ALL_ROLES: Role[] = [
  'Level1', 'Level2US', 'Level3EMEA', 'Level4', 'Level4Plus', 'Level5', 'SuperUser', 'SysAdmin',
  'Local',
];

/**
 * Does this caller reach anything at all? (Q32.)
 *
 * This REPLACED a hardcoded `roles.includes('Admin')` check, which gated the
 * entire app on one role name regardless of any privilege or grant — so
 * preview and production were Admin-only, and no page grant was observable
 * anywhere. It also hardcoded a role name, which `no-roles-in-code.test.ts`
 * forbids in packages/auth and admin-api but does not scan for in the SPA.
 *
 * Generic and fails closed: no pages, nowhere to go. A caller holding pages
 * gets in and the per-page and per-tab grants take over from there.
 */
export function reachesSomething(user: { grants?: { pages: string[] } } | null | undefined): boolean {
  return (user?.grants?.pages.length ?? 0) > 0;
}


export function AppShell() {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0];

  // MSAL is mid-interaction (startup, processing a redirect response, or an
  // in-flight login/acquireToken). Until this settles to `None`, freshly
  // returned tokens may not be in the cache yet — firing /api/me now races the
  // redirect handler and yields a spurious 401 (which would wrongly trip the
  // session-expired re-auth path). Wait it out instead.
  const msalBusy = inProgress !== InteractionStatus.None;

  // Dev-bypass picker — ungated entry point for local dev only. Lets you
  // switch between the four roles without touching Entra. Hidden in
  // staging/production builds (gated on import.meta.env.DEV at the
  // SignInScreen + TopBar render sites).
  // A LIST — the picker is multi-select, because Entra can assign a user
  // several app roles and the server unions their grants. `null` means no
  // bypass session; a non-empty array is the set being impersonated.
  const [devBypassRoles, setDevBypassRoles] = useState<readonly Role[] | null>(null);
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarHidden, toggleSidebar] = useSidebarHidden();

  // Dev-bypass role changes must drive the SERVER's view of the caller, not
  // only the SPA's. The picker fabricates its user client-side
  // (`syntheticUser` below); without the header the API keeps answering as
  // whatever DEV_AUTH_ROLES said at startup, so `_access` describes the wrong
  // caller and the UI offers edits a real Viewer would be refused.
  //
  // Both entry points route through here — the sign-in screen's picker and the
  // top-bar switcher — so they cannot drift apart.
  const applyDevBypassRole = (roles: readonly Role[]) => {
    setDevBypassRoles(roles);
    // Sign-in grants EVERY role locally, but the session starts narrowed to
    // SuperUser alone (Craig, 2026-08-19): holding everything means holding
    // SysAdmin and Local too, so the default would show technical surfaces no
    // business operator has. SuperUser is the widest BUSINESS role.
    //
    // ⚠️ Only when the caller signed in with MORE than SuperUser, and only if
    // they hold it. `signIn(page, url, 'Level1')` in the e2e must land as
    // Level1 — narrowing a deliberately-chosen role set to SuperUser would
    // override the very thing the caller asked for, and did: it turned
    // `ap.e2e.ts`'s Viewer-cannot-reach spec into a SuperUser session.
    // ⚠️ ONLY when sign-in granted EVERY role — i.e. the pre-ticked default,
    // which is the case this exists for. A caller who deliberately picked a
    // SUBSET asked for exactly that set, and narrowing it further overrides
    // the choice: `signIn(page, url, [SuperUser, Level1])` in the e2e dropped
    // Level1 before the spec touched anything, breaking two view-as tests.
    const narrowToSuperUser =
      roles.length === ALL_ROLES.length && roles.includes('SuperUser' as Role);
    setViewAsRoles(narrowToSuperUser ? (['SuperUser'] as Role[]) : null);
    setViewAsRoleHeader(narrowToSuperUser ? 'SuperUser' : null);
    setDevBypassRoleHeader(roles.length ? roles.join(',') : null);
    // Cached responses were fetched as the PREVIOUS role, so their `_access`
    // and their contents both belong to someone else.
    //
    // ⚠️ `clear()`, NOT `invalidateQueries()`. Invalidation marks data stale
    // and refetches, but LEAVES IT IN THE CACHE meanwhile — and any hook using
    // `placeholderData: (prev) => prev` (the downline table, so its rows don't
    // blank on every keystroke) will happily serve those rows during the
    // refetch. When the new role is refused, the refetch 403s, nothing arrives
    // to replace them, and the PREVIOUS role's customer rows stay on screen.
    //
    // Observed 2026-08-17: view a downline as Admin, switch to Viewer, and
    // Admin's rows keep rendering. Nothing escaped the server — Viewer's own
    // request was correctly refused — but the browser is showing one role's
    // data to another. A role switch is closer to a sign-out than a refresh,
    // so drop the cache outright.
    qc.clear();
  };

  // "View as" — the roles the operator has chosen to NARROW this session to.
  // `null` means no narrowing (the normal state); a non-empty array is the
  // subset being viewed as.
  //
  // Unlike the dev-bypass picker above this is NOT local-only: it works in
  // every environment, because it grants nothing. The server intersects the
  // header with the token's own roles, so the worst a tampered value can do is
  // narrow the session further. Staging is the environment it was built for.
  // Sign-in grants EVERY role locally, but the session starts narrowed to
  // SuperUser alone (Craig, 2026-08-19): holding everything means holding
  // SysAdmin and Local too, so the default session would see technical
  // surfaces no business operator has. SuperUser is the widest BUSINESS role,
  // which is the useful default; the rest are one click away in the toggles.
  //
  // ⚠️ `null` (unnarrowed) on every deployed environment. There the token
  // decides what you hold, and pre-narrowing a real session would hide
  // surfaces the operator was actually granted.
  const [viewAsRoles, setViewAsRoles] = useState<readonly Role[] | null>(null);

  const applyViewAsRoles = (roles: readonly Role[] | null) => {
    setViewAsRoles(roles);
    setViewAsRoleHeader(roles?.length ? roles.join(',') : null);
    // Same reasoning as the dev-bypass switch above, and the same `clear()`
    // rather than `invalidateQueries()`: cached rows were fetched as the WIDER
    // role, and any hook using `placeholderData: (prev) => prev` will keep
    // serving them while the narrowed refetch is in flight. If the narrow role
    // is then refused, nothing arrives to replace them and the previous role's
    // data stays on screen — the exact defect observed on 2026-08-17.
    //
    // Nothing escapes the server either way; this is about not showing one
    // role's data under another role's label.
    qc.clear();
  };

  // Routes that opt out of the 1400px content cap (desktop-only review pages
  // that need horizontal room). Hooks must run before any early returns,
  // so `useMatch` lives up here even though the value is only consumed
  // after the auth/permission gates resolve.
  const isFullWidth = useMatch('/system/engine/runs/:requestId/tree/:customerId') !== null;

  const realSignedIn = !!account;
  const devBypassActive = devBypassRoles !== null && devBypassRoles.length > 0;

  // Real auth path: fetch /api/me. A 403 / role-less response → no-access;
  // a 401 means the session lapsed → re-authenticate (see the gate below).
  // Gated on `!msalBusy` so we don't fetch before the token cache has settled.
  //
  // Also enabled under the DEV BYPASS. The picker sets `x-dev-auth-roles`, so
  // /api/me answers as the picked role and returns that role's REAL grants —
  // which is the only way the SPA can know them, since roles.jsonc is read
  // with readFileSync and cannot bundle.
  const meQuery = useCurrentUser({
    enabled: (realSignedIn || devBypassActive) && !msalBusy,
    devRole: devBypassRoles?.join(',') ?? null,
    viewAs:  viewAsRoles?.join(',') ?? null,
  });

  // A successful /api/me means we hold a valid token — the session is healthy,
  // so clear the one-shot re-auth guard. (A future timeout then gets a fresh
  // auto-redirect attempt rather than the manual fallback.)
  useEffect(() => {
    if (meQuery.isSuccess || devBypassActive) {
      sessionStorage.removeItem(REAUTH_FLAG);
    }
  }, [meQuery.isSuccess, devBypassActive]);

  if (!realSignedIn && !devBypassActive) {
    // MSAL still working (e.g. processing a redirect on the way back from
    // Entra) — wait rather than flashing the sign-in screen.
    if (msalBusy) {
      return <FullScreenSpinner label="Signing in…" />;
    }
    return <SignInScreen onDevBypass={applyDevBypassRole} />;
  }

  // Real-auth loading state: MSAL mid-interaction, or /api/me in flight.
  if (realSignedIn && (msalBusy || meQuery.isLoading)) {
    return <FullScreenSpinner label="Checking permissions…" />;
  }

  // Dev-bypass SWITCHES wait for /api/me too, and for the same reason.
  //
  // ⚠️ The race this closes: `syntheticUser` deliberately fabricates every page
  // and tab so the shell can render before /api/me lands (see its comment).
  // That is right at sign-in, when there is no prior state — but on a role
  // SWITCH it means one render where the SPA believes the new role reaches
  // everything. Every tab hook sees `mayReach === true`, fires, and the
  // requests land as 403s after /api/me narrows the grants. Nothing leaks —
  // the server refuses each one — but the console fills with refusals that
  // look like a broken app.
  //
  // Waiting is the honest answer: until the server says what this role
  // reaches, we do not know. Same spinner as the real-auth path above.
  //
  // `isLoading` (not `isFetching`) is what makes this a SWITCH-only wait: the
  // query is keyed by devRole, so a switch has no cached data and is genuinely
  // loading, while a background refetch of an already-answered role is not.
  if (mustAwaitGrants({ devBypassActive, meIsLoading: meQuery.isLoading })) {
    return <FullScreenSpinner label="Checking permissions…" />;
  }

  // staging + production both enforce the grants gate via strictRoles.
  // Local dev (non-strict) lets the dev-bypass role picker drive what the
  // session sees, and any signed-in MSAL user is synthesised as the broadest
  // role too — we don't want local work to depend on Entra App Role
  // assignments.
  const strict = appConfig.strictRoles;

  let currentUser: CurrentUser | null = null;
  if (devBypassActive) {
    // Identity is fabricated locally, but GRANTS come from the server whenever
    // it has answered — see syntheticUser for why the two halves are sourced
    // differently.
    const synthetic = syntheticUser(devBypassRoles!);
    currentUser = meQuery.data
      // `roles` comes from the SERVER so it reflects any "view as" narrowing
      // (that is what the badges show). `heldRoles` must stay the full bypass
      // selection: it is the picker's option list, and sourcing it from the
      // narrowed response would shrink the list to the current selection —
      // leaving no way to widen back.
      ? { ...synthetic, roles: meQuery.data.roles, heldRoles: [...devBypassRoles!], grants: meQuery.data.grants }
      : { ...synthetic, heldRoles: [...devBypassRoles!] };
  } else if (meQuery.data && reachesSomething(meQuery.data)) {
    currentUser = meQuery.data;
  } else if (!strict && realSignedIn) {
    // Signed in, non-strict (local dev) — synthesise Admin so dev work
    // doesn't depend on Entra App Role assignments.
    currentUser = {
      ...syntheticUser([ALL_ROLES[0]!]),
      upn: meQuery.data?.upn ?? account?.username,
      name: meQuery.data?.name ?? account?.name,
    };
  }

  const meError = meQuery.error instanceof HttpError ? meQuery.error : null;

  // 401 = not authenticated: the access token expired or couldn't be acquired
  // silently (jwtPlugin returns 401 for both missing and invalid/expired
  // bearer tokens). This is a lapsed session, NOT a missing role — an assigned
  // user whose session timed out hits this. Re-authenticate instead of showing
  // the "no role assigned" screen.
  const sessionExpired =
    strict && realSignedIn && !devBypassActive && meError?.status === 401;

  if (sessionExpired) {
    return (
      <SessionExpiredPage
        onReauth={() => void instance.acquireTokenRedirect(loginRequest)}
        onSignOut={() => instance.logoutRedirect()}
      />
    );
  }

  // Genuine no-access: forbidden (403), or an authenticated response that
  // reaches no page at all.
  //
  // ⚠️ This WIDENS access on preview and production. The check it replaced was
  // `roles.includes('Admin')`, so those environments admitted exactly one
  // role; all four placeholder roles hold pages, so all four now reach the
  // app — with grants that are deliberately arbitrary until the business
  // supplies the real role vocabulary.
  const noAccess =
    strict &&
    realSignedIn &&
    !devBypassActive &&
    (
      meError?.status === 403 ||
      (meQuery.isSuccess && !reachesSomething(meQuery.data))
    );

  if (noAccess || !currentUser) {
    return (
      <NoAccessPage
        upn={account?.username ?? meQuery.data?.upn}
        onSignOut={() => instance.logoutRedirect()}
      />
    );
  }

  // Bare `dev@local` — the roles are already on screen, ticked, right beside
  // it. Appending them here made the bar state one fact three times (badge,
  // checkboxes, parenthetical), and it grew with every role held.
  const username = devBypassActive
    ? 'dev@local'
    : currentUser.upn ?? account?.username;
  // ⚠️ Sign out must FORGET the remembered set, not just drop the session.
  // The picker auto-enters from localStorage, so without this the screen
  // signs you straight back in and Sign out reads as broken. It is also the
  // only route back to the picker: a reload keeps localStorage (that is what
  // it is for), and a hard reload bypasses the HTTP cache, not site data.
  const onSignOut = devBypassActive
    ? () => { forgetDevRoles(); setDevBypassRoles(null); }
    : () => instance.logoutRedirect();

  // Affordance only: the capture endpoint deliberately accepts any signed-in
  // operator, because it also carries automatic error telemetry (see the
  // `report_bug` entry in the operations registry). This decides whether the
  // control is OFFERED.
  const canBugReport = (currentUser.grants?.operations ?? []).includes('report_bug');

  return (
    <CurrentUserProvider user={currentUser} devBypass={devBypassActive}>
      <TimeZoneProvider>
        <ScrollRestoreMount />
        <div className="flex min-h-screen flex-col bg-gray-50">
          <TopBar
            username={username}
            roles={currentUser.roles}
            heldRoles={currentUser.heldRoles ?? currentUser.roles}
            viewAsRoles={viewAsRoles}
            onViewAsChange={applyViewAsRoles}
            onSignOut={onSignOut}
            showBugReport={canBugReport}
            onOpenMenu={() => setMenuOpen(true)}
          />
          <NavDrawer
            pages={currentUser.grants?.pages ?? []}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          />
          <div
            className={clsx(
              'mx-auto flex w-full flex-1 gap-6 px-4 pt-3 pb-8 sm:px-6 lg:px-8',
              // Routes that need horizontal room (e.g. the partner-tree review)
              // opt out of the 1400px cap. Add new fullWidth routes by extending
              // the useMatch list above.
              isFullWidth ? 'max-w-none' : 'max-w-[1400px]',
            )}
          >
            <Sidebar pages={currentUser.grants?.pages ?? []} hidden={sidebarHidden} />
            <div className="relative min-w-0 flex-1">
              <SidebarToggle hidden={sidebarHidden} onToggle={toggleSidebar} />
              <main className="min-w-0">
                <Outlet />
              </main>
            </div>
          </div>
        </div>
      </TimeZoneProvider>
    </CurrentUserProvider>
  );
}

// Scroll restore is a hook (not a component) and needs the router's
// location. Rendering an empty component is the simplest way to mount the
// hook from `AppShell`.
function ScrollRestoreMount() {
  useScrollRestore();
  return null;
}

/**
 * The dev-bypass identity, for the local role picker only.
 *
 * ⚠️ The `grants` here are a PLACEHOLDER — the whole registry — and the caller
 * is expected to REPLACE them with /api/me's answer as soon as it arrives.
 * They exist only to fill the first render before that response lands.
 *
 * The SPA cannot resolve grants itself: `roles.jsonc` is read with
 * `readFileSync` and cannot bundle, and re-deriving authorization in the
 * browser is the `can()` problem being removed (Q23). The server is therefore
 * the only source — and it can answer for the picked role, because the picker
 * sends `x-dev-auth-roles`.
 *
 * ⚠️ Do NOT let these placeholder grants reach a reach CHECK. They open every
 * page and every tab, so `reachesTab()` returns true for everything and the UI
 * disagrees with the API: the tab strip offers a tab the GET refuses, and
 * RequireTab waves through a URL the server 403s. That was the state before
 * grants were sourced from /api/me (2026-08-16), and it produced a raw
 * "403 Forbidden" in the card body instead of the not-available panel.
 *
 * This is a DEV-ONLY affordance and is not a gate. A real session's grants
 * always come from /api/me.
 */
/**
 * Whether the shell must hold on a spinner rather than render with fabricated
 * grants. Exported for test — the condition is one line, but the reasoning it
 * encodes is the whole fix, and an inline `&&` cannot be asserted.
 *
 * See the call site for why a dev-bypass role switch has to wait.
 */
export function mustAwaitGrants(
  { devBypassActive, meIsLoading }: { devBypassActive: boolean; meIsLoading: boolean },
): boolean {
  return devBypassActive && meIsLoading;
}

function syntheticUser(roles: readonly Role[]): CurrentUser {
  // Identity is stamped from the FIRST role, matching the server's synthetic
  // OID (`DEV_ROLE_OIDS` in jwt.ts, same rule) so audit rows agree with the
  // claims. The real grants come from /api/me regardless.
  const primary = roles[0]!;
  return {
    upn: `dev+${primary.toLowerCase()}@local`,
    name: `Dev ${roles.join(' + ')}`,
    roles: [...roles],
    // ⚠️ `operations` and `lists` are EMPTY, not filled like pages and tabs —
    // the two halves fail in opposite directions on purpose.
    //
    // Opening every page and tab makes the shell render before /api/me lands,
    // which is what this placeholder is for. Opening every OPERATION would
    // fabricate the right to ACT: an Add button enabled on a first render,
    // clickable, against a server that refuses. Fabricating reach shows a
    // surface early; fabricating an operation invites an action that fails.
    //
    // Empty is also the honest default for lists, since a list id names a
    // projection and the registry deliberately holds almost none — filling it
    // from the registry would grant exactly the handful that happen to be
    // picked, which is meaningless rather than permissive.
    grants: { pages: PAGES.map((p) => p.id), tabs: [...TAB_IDS], operations: [], lists: [] },
  };
}

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <LoadingSpinner />
        <p className="mt-3 text-sm text-gray-600">{label}</p>
      </div>
    </div>
  );
}

/**
 * "View as" — narrow this session to a subset of the roles you actually hold.
 *
 * Renders ONLY when the operator holds more than one role: with a single role
 * there is nothing to narrow to, and an inert control that cannot change
 * anything is worse than no control.
 *
 * ⚠️ This is a NARROWING filter, not a role switcher. The server intersects the
 * selection with the token's own roles, so an operator can only ever see LESS
 * than they are entitled to. Nothing here grants anything, which is why it ships
 * to staging and production alike rather than hiding behind `import.meta.env.DEV`
 * the way the dev-bypass picker does.
 *
 * ⚠️ It narrows REACH AND FIELDS together (Craig, 2026-08-18) — the sidebar and
 * route guards via `grants`, and per-field masking/editability via `_access`,
 * both computed server-side from the one narrowed role list. A reach-only
 * version was rejected: it would show the narrow role's screens filled with the
 * wide role's data, so an operator could sign off on a masking bug that is
 * genuinely there.
 *
 * "Reset" clears the narrowing rather than re-ticking every box, because the
 * unnarrowed state is not the same thing as "all roles selected" — it sends no
 * header at all.
 */
/**
 * The role toggles — one pill per held role, green on / grey off.
 *
 * Replaced a trigger button plus dropdown panel (Craig, 2026-08-19). Three
 * things drove it:
 *
 * 1. **The panel could not stay open.** Ticking a role clears the query cache,
 *    so `/api/me` has no data for the new selection and `mustAwaitGrants`
 *    swaps the whole shell for a full-screen spinner. That spinner is
 *    DELIBERATE — it stops the SPA rendering one frame that believes the new
 *    role reaches everything — but it took the panel's `open` state with it,
 *    so narrowing four roles meant opening the panel four times.
 * 2. **The trigger's label repeated the badge row.** `Viewing as: Level2US,
 *    Level3EMEA, Level4` sat beside badges saying exactly that, and grew until
 *    it wrapped to three lines.
 * 3. **A control with no open state cannot lose it.** Pills are always there.
 *
 * ⛔ These only ever toggle roles the caller ALREADY HOLDS. Switching one back
 * on re-asserts a grant from sign-in; it cannot add a role that was never
 * granted. The server intersects the header with the token's own roles, so a
 * tampered value narrows further at worst.
 *
 * The badges are kept alongside deliberately (Craig): they are what deployed
 * environments show, and losing them here would mean local no longer matches.
 */
function ViewAsToggles({
  heldRoles, viewAsRoles, onChange,
}: {
  heldRoles: readonly Role[];
  viewAsRoles: readonly Role[] | null;
  onChange: (roles: readonly Role[] | null) => void;
}) {
  // Nothing to toggle.
  if (heldRoles.length < 2) return null;

  return (
    <div
      // 3×3 grid (Craig, 2026-08-19). Nine roles in one row ran the width of
      // the bar and pushed everything else off; three rows of three keeps the
      // control compact and every role visible at once — no scrolling, no
      // wrapping that reflows as roles toggle.
      //
      // Fills ACROSS each row before wrapping (Craig, 2026-08-19), so the
      // roles read left-to-right in roles.jsonc order — Level1/Level2US/
      // Level3EMEA, then Level4/Level4Plus/Level5, and so on. Column-first put
      // Level4 beside Level1, which reads as a grouping that means nothing.
      className="grid grid-cols-3 gap-x-1 gap-y-0.5"
      data-testid="view-as-toggles"
      title="Click a role to switch it off for this session. You can only toggle roles you already hold."
    >
      {heldRoles.map((r) => {
        // No narrowing means every held role is on — `null` is not the same as
        // "all selected", it means no header goes out at all.
        const on = viewAsRoles === null ? true : viewAsRoles.includes(r);
        return (
          <button
            key={r}
            type="button"
            aria-pressed={on}
            onClick={() => {
              const base = viewAsRoles ?? heldRoles;
              const next = on ? base.filter((x) => x !== r) : [...base, r];
              // A session holding nothing reaches nothing, which renders as a
              // broken app rather than a deliberate state. Refuse it.
              if (!next.length) return;
              // Everything on IS the unnarrowed state — send null so no header
              // goes out.
              onChange(next.length === heldRoles.length ? null : next);
            }}
            className={clsx(
              'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
              on
                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
            )}
          >
            {r}
          </button>
        );
      })}
    </div>
  );
}

/** The environment's name, as an operator would say it. */
function envLabel(environment: string): string {
  switch (environment) {
    case 'development': return 'local';
    case 'staging':     return 'staging';
    case 'preview':     return 'preview';
    case 'production':  return 'production';
    // An unrecognised value is shown VERBATIM rather than guessed at — seeing
    // `prod` under the logo is how a typo in APP_ENVIRONMENT gets noticed.
    default:            return environment;
  }
}

/** Matches the chrome, so the word and the colour never disagree. */
function envLabelTone(environment: string): string {
  switch (environment) {
    case 'development': return 'text-red-700';
    case 'staging':     return 'text-red-700';
    case 'preview':     return 'text-red-700';
    default:            return 'text-gray-500';
  }
}

/**
 * Header chrome by environment — so you can tell WHERE YOU ARE at a glance.
 *
 * ⚠️ Deliberately inverted from the obvious mapping (Craig, 2026-08-19).
 * Production is the CALM one: people work in it all day, and a permanent red
 * banner on the environment someone uses for eight hours is noise they stop
 * seeing — at which point it warns nobody. The loud colour goes on every
 * other environment, where a jolt of "this is not real" is useful and where
 * work done is not kept. Bold red text across the header, under its controls
 * (`TestInstanceNotice`) says so in words.
 *
 *   production   neutral   the working environment
 *   preview      red       real unmasked data, but a copy the next clone replaces
 *   staging      red       shared, safe to break
 *   local        red       fabricated identities, throwaway data
 *
 * The three red environments differ only in the name under the logo.
 *
 * ⚠️ An UNRECOGNISED value falls through to production's neutral chrome, and
 * that is the safe direction for a typo — `APP_ENVIRONMENT=prod` renders calm
 * rather than shouting "local" at someone in production. config.ts:46 records
 * the same hazard for `strictRoles`, which fails open the other way.
 */
function envChrome(environment: string): string {
  switch (environment) {
    case 'development': return 'border-red-300 bg-red-50';
    case 'staging':     return 'border-red-300 bg-red-50';
    case 'preview':     return 'border-red-300 bg-red-50';
    default:            return 'border-gray-200 bg-white';
  }
}

function TopBar({
  username, roles, heldRoles, viewAsRoles, onViewAsChange,
  onSignOut, showBugReport, onOpenMenu,
}: {
  /** Below `lg`, where there is no sidebar: open the menu panel. */
  onOpenMenu: () => void;
  username?: string;
  roles: Role[];
  /** Everything the token carries — the toggles' option list. */
  heldRoles: readonly Role[];
  viewAsRoles: readonly Role[] | null;
  onViewAsChange: (roles: readonly Role[] | null) => void;
  onSignOut: () => void;
  showBugReport: boolean;
  // `devBypass` / `devBypassRoles` / `onDevBypassRoleChange` are GONE with the
  // amber checkbox row they drove (2026-08-19). Sign-in sets what you hold now,
  // so the bar needs no bypass-specific props at all.
}) {
  return (
    // `relative z-30` gives the bar its own stacking context above page
    // content. Without it the "view as" dropdown (z-20, but inside the header's
    // flow) rendered UNDER page inputs — the Customers search box swallowed
    // clicks on "Reset to all my roles", so an operator could open the panel,
    // see Reset, click it, and have nothing happen.
    <header className={clsx('relative z-30 border-b', envChrome(appConfig.environment))}>
      {/* `min-h-14`, not `h-14` (2026-08-19): the role toggles are a 3-row grid
          and a fixed height clipped them. The bar keeps its old height whenever
          the toggles are absent — deployed environments with a single-role
          token, and every page for a caller who holds one role. */}
      <div className="relative mx-auto flex min-h-14 w-full max-w-[1400px] items-center justify-between gap-3 px-4 py-1 sm:px-6 lg:px-8">
        {/* The dev role picker lives on the LEFT, beside the logo, so the
            right-hand side of the header is byte-for-byte what production
            renders. A dev-only control that displaces production chrome makes
            local a worse model of the thing being verified.

            No "dev bypass" chip either: these checkboxes render ONLY under
            bypass, so their presence already says it — in amber, in the same
            bar. One indicator, not two. */}
        <div className="flex items-center gap-3">
          {/* Below `lg` the sidebar is not shown, so this opens the menu panel.
              At `lg` and wider the sidebar has its own « / » toggle
              (`SidebarToggle`). The wrapper carries the breakpoint because
              Button sets its own `display`. */}
          <span className="lg:hidden">
            <Button variant="ghost" size="sm" aria-label="Open menu" onClick={onOpenMenu}>☰</Button>
          </span>
          {/* Logo + the environment NAME beneath it (Craig, 2026-08-19).
              The chrome colour alone asks you to remember which colour means
              what; the word removes that. They are redundant on purpose —
              colour is what you notice, the name is what you check. */}
          <span className="inline-flex flex-col leading-tight">
            <Logo />
            <span className={clsx('pl-[46px] text-lg font-bold uppercase tracking-wide', envLabelTone(appConfig.environment))}>
              {envLabel(appConfig.environment)}
            </span>
          </span>
          {/* ⛔ The amber dev-bypass checkbox row was REMOVED (Craig, 2026-08-19).
              It set which roles you HOLD — a job sign-in now does, since its
              picker pre-ticks every role, so a local session starts as a full
              operator and these toggles take it from there.

              Two controls for one job, one of them local-only, is what made
              this bar confusing: the checkboxes and the view-as panel were
              both lists of role names doing opposite things.

              ⬅ LEFT, beside the logo. The right-hand side is status (badges,
              timezone, sign-out); the thing that CHANGES the session belongs
              with the logo, not among the readouts. */}
          <ViewAsToggles
            heldRoles={heldRoles}
            viewAsRoles={viewAsRoles}
            onChange={onViewAsChange}
          />
        </div>
        {/* One line, centred across the whole bar, painted under the left group
            and the right column. `-z-10` puts it below the bar's in-flow
            content but above the header's background, because the header is
            the stacking context. The role toggles and badges cover parts of it
            for a caller who holds more than one role. */}
        <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden whitespace-nowrap">
          <TestInstanceNotice environment={appConfig.environment} />
        </div>
        {/* TWO ROWS (Craig, 2026-08-19), split by how often you look at them:
            WHO you are on top, and the tools underneath. One row of six items
            gave the identity no more weight than the timezone picker. */}
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-3">
            {/* These are the EFFECTIVE roles, so under a narrowing the badges
                show the narrowed set — the honest answer to "who am I right
                now", and why the toggles need no separate indicator.

                `data-testid` so e2e can wait for a role switch to LAND: they
                render from the /api/me payload, so they change only once the
                refetch resolves — exactly when the SPA's other capabilities
                become valid too. */}
            <div className="flex items-center gap-1" data-testid="role-badges">
              {roles.map((r) => (
                <Badge key={r} tone={roleTone(r)}>{r}</Badge>
              ))}
            </div>
            <span className="hidden text-sm text-gray-600 sm:inline">{username}</span>
            <Button variant="ghost" size="sm" onClick={onSignOut}>
              Sign out
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <TimeZoneSelect />
            {showBugReport && <BugReportButton />}
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * The sidebar IS the page registry, filtered by what the caller holds.
 *
 * There is no second list to maintain and nothing to keep in sync: a page
 * carries its own sidebar LABEL (`nav`), and its presence is the flag — so
 * "does this appear in the sidebar?" is a property of the page, while "may I
 * open it?" is a property of the role, and the two cannot contradict.
 *
 * This replaced a hand-written NAV array whose entries each named an old
 * privilege. Two of them were provably wrong and said so in their own
 * comments: Commissions and Reports both required `customers:read`, the
 * broadest read grant in the system, so anyone who could open a customer saw
 * both links whether or not they could use either.
 *
 * Affordance only, and unchanged in that respect — the API refuses regardless,
 * and a page a role does not hold renders the not-available panel on a direct
 * URL. What this removes is the SPA offering a door it knows is locked.
 */
// Exported for component testing — see app-shell.test.tsx.
//
// Shown at `lg` and wider unless the operator hid it. Below `lg` the menu panel
// (`NavDrawer`) carries the same links. The aside stays in the DOM either way,
// so a bug report still captures the nav region.
export function Sidebar({ pages, hidden = false }: { pages: string[]; hidden?: boolean }) {
  return (
    // `w-max`: as wide as the widest link the caller holds, and no wider.
    <aside className={clsx('w-max shrink-0', hidden ? 'hidden' : 'hidden lg:block')}>
      <NavLinks pages={pages} />
    </aside>
  );
}

/**
 * Hides (« ) or shows (» ) the sidebar, at `lg` and wider.
 *
 * It hangs off the main block's top-left corner, so it is in the same place
 * relative to the main block in both states: in the gap beside the first
 * sidebar link when the sidebar shows, and in the page's left padding when it
 * is hidden. Its 20 px width plus the 2 px margin must fit inside the 24 px
 * gap (`gap-6`) and the 32 px padding (`lg:px-8`) of the row around it.
 * Its 36 px height matches one sidebar link.
 */
function SidebarToggle({ hidden, onToggle }: { hidden: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label={hidden ? 'Show sidebar' : 'Hide sidebar'}
      aria-pressed={hidden}
      title={hidden ? 'Show sidebar' : 'Hide sidebar'}
      onClick={onToggle}
      className="absolute right-full top-0 mr-0.5 hidden h-9 w-5 items-center justify-center rounded text-base text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 lg:flex"
    >
      {hidden ? '»' : '«'}
    </button>
  );
}

/** The sidebar's links, shared by the sidebar and the menu panel. */
function NavLinks({ pages, onNavigate }: { pages: string[]; onNavigate?: () => void }) {
  const held = new Set(pages);
  const visible = PAGES.filter((p) => p.nav && held.has(p.id))
    .map((p) => ({ to: p.path, label: p.nav as string }));
  return (
    <nav className="flex flex-col gap-1">
      {visible.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            clsx(
              'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-50 text-brand-700'
                : 'text-gray-700 hover:bg-gray-100',
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * The menu panel: the sidebar's links in a native `<dialog>` that slides in
 * from the left. It closes on Esc, on a click outside the panel, and when a
 * link is followed.
 */
// Exported for component testing — see app-shell.test.tsx.
export function NavDrawer({ pages, open, onClose }: {
  pages: string[];
  open: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label="Menu"
      onClose={onClose}
      // A click on the dialog element itself is a click on its backdrop: the
      // inner div fills the panel, so a click inside lands on the div.
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
      className="m-0 h-full max-h-none w-64 max-w-[80vw] bg-white p-0 shadow-xl backdrop:bg-black/30"
    >
      <div className="h-full overflow-y-auto p-4">
        <NavLinks pages={pages} onNavigate={onClose} />
      </div>
    </dialog>
  );
}

/** Where the operator's hide-the-sidebar choice is kept. */
export const SIDEBAR_HIDDEN_LS_KEY = 'jpc.sidebarHidden';

function readSidebarHidden(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_HIDDEN_LS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Whether the operator hid the sidebar, and a toggle. The choice survives a
 * reload. ⚠️ localStorage throws in a private window and can be blocked
 * outright; the choice then lasts until the reload.
 */
export function useSidebarHidden(): [boolean, () => void] {
  const [hidden, setHidden] = useState(readSidebarHidden);
  const toggle = () => {
    const next = !hidden;
    setHidden(next);
    try {
      if (next) window.localStorage.setItem(SIDEBAR_HIDDEN_LS_KEY, '1');
      else window.localStorage.removeItem(SIDEBAR_HIDDEN_LS_KEY);
    } catch {
      /* storage disabled — the choice lasts until the reload */
    }
  };
  return [hidden, toggle];
}

/**
 * Where the dev picker remembers the last role set.
 *
 * Namespaced like the SPA's other local settings (`jpc.displayTimeZone`).
 */
const DEV_ROLES_LS_KEY = 'jpc.devBypassRoles';

/**
 * The role set a previous local session entered with, or `null`.
 *
 * Returns null — never a partial set — for anything unexpected: no entry, a
 * production build, unreadable storage, malformed JSON, or a stored role that
 * `ALL_ROLES` no longer contains. A half-recognised set would sign you in as
 * somebody you did not choose, which is worse than showing the picker.
 *
 * ⚠️ localStorage throws in a private window and can be blocked outright, so
 * every access is wrapped. It also never reaches the server: the roles it holds
 * are re-sent as a header by `applyDevBypassRole`, and the API decides.
 */
function rememberedDevRoles(): readonly Role[] | null {
  if (!import.meta.env.DEV) return null;
  try {
    const raw = window.localStorage.getItem(DEV_ROLES_LS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Every entry must still be a real role. A role deleted from ALL_ROLES
    // (as 'Admin' was, 2026-08-18) would otherwise resurrect itself here and
    // start the session deny-by-default.
    const roles = parsed.filter((r): r is Role => ALL_ROLES.includes(r as Role));
    return roles.length === parsed.length ? roles : null;
  } catch {
    return null;
  }
}

/** Remember this set for the next local session. Never throws. */
function rememberDevRoles(roles: readonly Role[]): void {
  if (!import.meta.env.DEV) return;
  try {
    window.localStorage.setItem(DEV_ROLES_LS_KEY, JSON.stringify(roles));
  } catch { /* private window, or storage disabled — the picker still works */ }
}

/** Forget the remembered set, so the next load shows the picker again. */
export function forgetDevRoles(): void {
  try {
    window.localStorage.removeItem(DEV_ROLES_LS_KEY);
  } catch { /* nothing to forget if storage is unreadable */ }
}

// Exported for component testing (the dev-bypass role picker). Rendered only
// by AppShell in the app.
export function SignInScreen({ onDevBypass }: { onDevBypass: (roles: readonly Role[]) => void }) {
  const { instance } = useMsal();
  // Multi-select — the server unions the grants of every role picked.
  //
  // The "Enforce auth" checkbox that used to sit here is GONE (2026-08-17).
  // Local always enforces now: an unenforced session handed every route
  // ALL_GRANTS, which is precisely the configuration in which a masking or
  // gating bug cannot be seen. Roles are still fabricated; only the escape
  // hatch went.
  // Seeded from ALL_ROLES rather than a literal: 'Admin' was deleted from the
  // Entra app registration on 2026-08-18, and a picker defaulting to a role
  // that no longer exists starts every local session deny-by-default.
  // ALL roles, not the first one (Craig, 2026-08-19). Sign-in GRANTS the set;
  // switching individual roles off is the top bar's job. Landing with one role
  // meant every local session began by ticking boxes to get back to a normal
  // operator, which is the opposite of what the picker is for.
  const [pickedRoles, setPickedRoles] = useState<readonly Role[]>(
    () => rememberedDevRoles() ?? [...ALL_ROLES],
  );

  // Auto-enter with the remembered set, so a reload — or a link opened in a new
  // tab — lands where it was pointed instead of at this screen. Without it every
  // navigation loses its destination: the sign-in screen replaces the route, and
  // entering drops you at `/`, which redirects to `/customers`.
  //
  // ⚠️ Only when a PREVIOUS session stored a set. A first-ever visit still sees
  // the picker, so the screen is never skipped for someone who has not chosen.
  //
  // ⚠️ `import.meta.env.DEV` guards the whole thing — same gate as the picker
  // below. A production build has no picker to remember, and must never
  // fabricate a session.
  //
  // ⚠️ This does NOT fire under Playwright: each test gets a fresh browser
  // context with empty localStorage, so `rememberedDevRoles()` is null and
  // `signIn()` finds the picker it waits for. That isolation is what makes this
  // safe — see the invariant test in app-shell.test.tsx.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const remembered = rememberedDevRoles();
    if (remembered) onDevBypass(remembered);
    // Once, on mount. `onDevBypass` is a new identity each render, so depending
    // on it would re-enter on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-sm rounded-[var(--radius-card)] bg-white p-8 shadow-[var(--shadow-card)]">
        <div className="text-center">
          <Logo size="lg" />
        </div>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in with your company account
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <UnauthenticatedTemplate>
            <Button
              onClick={() => instance.loginRedirect(loginRequest)}
              disabled={!msalConfigured}
              title={msalConfigured ? undefined : 'Entra config not loaded — use dev bypass'}
            >
              Sign in
            </Button>
          </UnauthenticatedTemplate>
          <AuthenticatedTemplate>
            <p className="text-center text-sm text-gray-600">Authenticating…</p>
          </AuthenticatedTemplate>
          {import.meta.env.DEV && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="mb-2 font-medium">Skip auth (dev) — pick one or more roles:</p>
              <div className="flex flex-col gap-1">
                {ALL_ROLES.map((r) => {
                  const held = pickedRoles.includes(r);
                  return (
                    <label key={r} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={held}
                        onChange={() => setPickedRoles(
                          held ? pickedRoles.filter((x) => x !== r) : [...pickedRoles, r],
                        )}
                      />
                      <span>{r}</span>
                    </label>
                  );
                })}
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2 w-full"
                disabled={pickedRoles.length === 0}
                onClick={() => { rememberDevRoles(pickedRoles); onDevBypass(pickedRoles); }}
              >
                Enter
              </Button>
              <p className="mt-2 text-amber-800">
                Grants are the UNION of every role picked, exactly as the server
                computes them. Auth is always enforced — each role sees a
                different set of nav items and permissions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
